import {
    RED_XL_GROUP,
    SAILS_PAGE_OPTIONS,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_SOHO,
} from '@vfde-fe/sails-constants';
import { getIsTradeInEligible } from '../../DeviceDetail/helpers/getIsTradeInEligible';
import {
    ApiPrice,
} from '../../DeviceDetail/interfaces/api';
import {
    Offer,
    StateProps,
} from '../../DeviceDetail/interfaces/state';

/**
 * Get monthly tariff price including all discounts
 * @param offer
 * @param gigakombi
 * @param isTradeIn
 * @param hasInsurance
 * @param salesChannel
 * @param getStairway
 */
export const calculateDiscountedMonthlyPrice = ({ offer, isGigakombi, isTradeIn, hasInsurance, salesChannel, hasSubscription}: StateProps, getStairway: boolean = false): number | undefined => {
    // We have nothing, go away
    if (!hasSubscription && !hasInsurance) {
        return;
    }

    // We can have an insurance, even if we don't have a subscription (yet), but we don't need to calculate then
    if (!hasSubscription && hasInsurance) {
        return offer!.insurance?.price.gross;
    }

    const { subLevelAmount = 0, discounts, insurance, tariffPrice } = offer as Offer;
    let { monetary: stairway } = offer as Offer;

    stairway ||= [];

    // Get either stairway (if present) or tariff price as base price to do further calculations on.
    let basePrice = stairway.length > 0 && !getStairway ? stairway[0].gross : tariffPrice!.gross;

    // Also, remove sublevel from base price for more generic calculations. Will be added back later, don't worry.
    basePrice -= subLevelAmount;

    const { salesChannel: discountForSalesChannel, gigakombi, promotions } = window[SAILS_PAGE_OPTIONS].discounts;
    const dummySocsForSalesChannel = Object.values(gigakombi.dummy[salesChannel!] || {});

    // Put all eligible discounts in an array
    const eligibleDiscounts = [];
    isEligibleForGigakombi(basePrice, salesChannel!, isGigakombi) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], [gigakombi.marker, ...dummySocsForSalesChannel])));
    isEligibleForFamilyFriends(salesChannel!) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], [discountForSalesChannel[SALESCHANNEL_FAMILY_FRIENDS]])));
    isEligibleForTradeIn(isTradeIn, salesChannel!) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], [ promotions.tradeIn ])));
    isEligibleForSoho(basePrice, salesChannel!) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], [discountForSalesChannel[SALESCHANNEL_SOHO].default, discountForSalesChannel[SALESCHANNEL_SOHO][RED_XL_GROUP]])));

    // Quickfix for iPhone Promo to hardcore discount id, discount will be ignored to damian when its not available on a device in vlux @TODO: remove when promo is gone
    eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], [promotions.iphone])));

    // Add default discounts
    eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts?.monetary || [], promotions.default)));

    // Put all additional costs in an array, like insurance
    const eligibleAddons = [subLevelAmount]; // Sublevel is back, you see
    hasInsurance && insurance?.price && eligibleAddons.push(insurance.price.gross);

    const price = [...eligibleDiscounts, ...eligibleAddons].reduce((previousValue, currentValue) => previousValue + currentValue, basePrice);

    return price > 0 ? roundToTwoDecimal(price) : 0;
};

/**
 * Calculate stairway price
 * @param state
 */
export const calculateMonthlyPriceWithoutStairway = (state: StateProps): number | undefined => {
    return calculateDiscountedMonthlyPrice(state, true);
};

/**
 * getMonetaryTradeIn
 */
export const getMonetaryDiscount = (monetaryDiscounts: ApiPrice[] = [], discountIds: string[] = []): number => {
    const monetaryDiscountsLength = monetaryDiscounts.length;
    let totalDiscount: number = 0;

    for (let i = 0; i < monetaryDiscountsLength; i++) {
        const monetaryDiscount = monetaryDiscounts[i];

        discountIds.forEach((id) => {
            if (monetaryDiscount.id === id) {
                totalDiscount += monetaryDiscount.gross;
            }
        });
    }

    return totalDiscount;
};

const isEligibleForGigakombi = (basePrice: number, salesChannel: SalesChannel, isGigakombi: boolean): boolean => {
    return isGigakombi && !isFamilyFriends(salesChannel) && basePrice > 0;
};

const isEligibleForTradeIn = (isTradeIn: boolean, salesChannel: SalesChannel): boolean => {
    return isTradeIn && getIsTradeInEligible(salesChannel);
};

const isEligibleForFamilyFriends = (salesChannel: SalesChannel): boolean => {
    return isFamilyFriends(salesChannel);
};

const isEligibleForSoho = (basePrice: number, salesChannel: SalesChannel): boolean => {
    return isSoho(salesChannel) && basePrice > 0;
};

const isSalesChannel = (currentSalesChannel: SalesChannel, salesChannelToCheck: SalesChannel): boolean => {
    return currentSalesChannel === salesChannelToCheck;
};

const isSoho = (salesChannel: SalesChannel): boolean => {
    return isSalesChannel(salesChannel, SALESCHANNEL_SOHO);
};

const isFamilyFriends = (salesChannel: SalesChannel): boolean => {
    return isSalesChannel(salesChannel, SALESCHANNEL_FAMILY_FRIENDS);
};

/**
 * Get strike price from offer
 */
export const getStrikePrice = ({ offer, hasInsurance = false, isTradeIn = false, isGigakombi = false }: StateProps): number | undefined => {
    if (!offer) {
        return;
    }

    let strikePrice = (offer as Offer).tariffPrice?.gross || 0;

    if (hasInsurance) {
        const gross = offer.insurance?.price.gross;

        if (gross) {
            strikePrice += gross;
        }
    }

    if (isTradeIn && (offer as Offer).discounts?.monetary) {
        strikePrice -= getMonetaryDiscount((offer as Offer).discounts.monetary, [window[SAILS_PAGE_OPTIONS].discounts.promotions.tradeIn]);
    }

    return roundToTwoDecimal(strikePrice);
};

/**
 * Check for stairway in offer
 * @param offer
 */
export const hasStairway = (offer: Offer): boolean => {
    const { monetary = [] } = offer;

    return monetary && monetary.length > 0;
};

/**
 * Check if a discount is "eligible" for a strike price
 * @param offer
 * @param salesChannel
 * @param isGigakombi
 */
export const shouldShowStrikePrice = ({ offer, salesChannel, isGigakombi = false }: Partial<StateProps>): boolean => {
    return !!offer && !hasStairway(offer as Offer) && ([SALESCHANNEL_SOHO, SALESCHANNEL_FAMILY_FRIENDS].indexOf(salesChannel!) !== -1 || isGigakombi);
};

const getNegativeValue = (value: number): number => value * -1;

const roundToTwoDecimal = (numberToRound: number) => Math.round(numberToRound * 100) / 100;
